import { ulid } from "ulid";

/**
 * Represents a queued HTTP request that will be executed when the connection is restored
 */
export interface QueuedRequest {
  /** Unique identifier for the queued request */
  id: string;
  /** The URL to send the request to */
  url: string;
  /** HTTP method (GET, POST, PUT, DELETE, etc.) */
  method: string;
  /** HTTP headers to include with the request */
  headers: Record<string, string>;
  /** Serialized request body */
  body: string;
  /** Timestamp when the request was queued */
  timestamp: number;
}

/**
 * Event data emitted when a request is successfully processed and needs cache invalidation
 */
export interface RequestProcessedEvent {
  /** The processed request details */
  request: QueuedRequest;
  /** The successful response from the server */
  response: Response;
  /** Parsed response data if available */
  responseData?: unknown;
}

/**
 * Service for managing offline HTTP requests using IndexedDB storage and background sync.
 * Automatically queues failed requests and retries them when connectivity is restored.
 *
 * @example
 * ```typescript
 * const queue = new OfflineQueueService();
 * await queue.initialize();
 *
 * // Queue a request for later execution
 * const requestId = await queue.queueRequest(
 *   '/api/data',
 *   'POST',
 *   { 'Content-Type': 'application/json' },
 *   { message: 'Hello World' }
 * );
 *
 * // Process all queued requests
 * await queue.processQueue();
 * ```
 */
export class OfflineQueueService extends EventTarget {
  /** Name of the IndexedDB database */
  private dbName = "offline-queue";
  /** Version of the IndexedDB database schema */
  private dbVersion = 1;
  /** Name of the object store within the database */
  private storeName = "requests";
  /** Flag to prevent concurrent queue processing */
  private isProcessing = false;
  /** Reference to the IndexedDB database instance */
  private db: IDBDatabase | null = null;

  /**
   * Initializes the IndexedDB database and creates the necessary object stores.
   * Must be called before using other methods.
   *
   * @returns Promise that resolves when initialization is complete
   * @throws Error if IndexedDB initialization fails
   */
  async initialize(): Promise<void> {
    if (this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: "id" });
        }
      };
    });
  }

  /**
   * Adds an HTTP request to the offline queue for later execution.
   * Automatically registers a background sync event if supported.
   *
   * @param url - The URL to send the request to
   * @param method - HTTP method (GET, POST, PUT, DELETE, etc.)
   * @param headers - HTTP headers to include with the request
   * @param body - Request body data (will be JSON serialized)
   * @returns Promise that resolves to the unique request ID
   * @throws Error if database is not initialized or request cannot be queued
   */
  async queueRequest(
    url: string | URL,
    method: string,
    headers: Record<string, string>,
    body: unknown,
  ): Promise<string> {
    if (!this.db) await this.initialize();
    if (!this.db) throw new Error("Failed to initialize IndexedDB");

    const queuedRequest: QueuedRequest = {
      id: ulid(),
      url: url instanceof URL ? url.toString() : url,
      method,
      headers,
      body: body != null ? JSON.stringify(body) : "",
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.add(queuedRequest);

      request.onsuccess = () => {
        this.dispatchEvent(
          new CustomEvent("requestQueued", {
            detail: { requestId: queuedRequest.id, queuedRequest },
          }),
        );

        resolve(queuedRequest.id);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieves all queued requests from the database.
   *
   * @returns Promise that resolves to an array of all queued requests
   * @throws Error if database is not initialized or retrieval fails
   */
  async getAllQueuedRequests(): Promise<QueuedRequest[]> {
    if (!this.db) await this.initialize();
    if (!this.db) throw new Error("Failed to initialize IndexedDB");

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Removes a specific request from the queue by its ID.
   *
   * @param id - The unique identifier of the request to remove
   * @returns Promise that resolves when the request is removed
   * @throws Error if database is not initialized or removal fails
   */
  async removeRequest(id: string): Promise<void> {
    if (!this.db) await this.initialize();
    if (!this.db) throw new Error("Failed to initialize IndexedDB");

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        this.dispatchEvent(
          new CustomEvent("requestRemoved", {
            detail: { requestId: id },
          }),
        );
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Processes all queued requests by attempting to execute them.
   * Successfully executed requests are removed from the queue.
   * Failed requests are also removed (no automatic retry).
   *
   * @returns Promise that resolves when all requests have been processed
   * @throws Error if database operations fail
   */
  async processQueue(): Promise<void> {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const queuedRequests = await this.getAllQueuedRequests();

      for (const queuedRequest of queuedRequests) {
        try {
          const response = await fetch(queuedRequest.url, {
            method: queuedRequest.method,
            headers: queuedRequest.headers,
            body: queuedRequest.body || undefined,
          });

          if (response.ok) {
            await this.removeRequest(queuedRequest.id);

            // Clone response before consuming the body (response body can only be read once)
            const responseClone = response.clone();

            let responseData: unknown;
            try {
              const contentType = response.headers.get("content-type");
              if (contentType?.includes("application/json")) {
                responseData = await response.json();
              }
            } catch (error) {
              console.warn(
                `Failed to parse response JSON for request ${queuedRequest.id}:`,
                error,
              );
            }

            this.dispatchEvent(
              new CustomEvent("requestProcessed", {
                detail: {
                  request: queuedRequest,
                  response: responseClone,
                  responseData,
                } as RequestProcessedEvent,
              }),
            );
          } else {
            await this.handleFailedRequest(
              queuedRequest,
              `HTTP ${response.status}`,
            );
          }
        } catch (error) {
          await this.handleFailedRequest(
            queuedRequest,
            error instanceof Error ? error.message : "Network error",
          );
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Handles a failed request by removing it from the queue.
   * We don't retry failed requests - if they fail after coming back online,
   * there's likely a legitimate issue (auth expired, validation error, etc.)
   *
   * @param request - The failed request object
   * @param error - Error message describing the failure
   * @returns Promise that resolves when the failure is handled
   * @private
   */
  private async handleFailedRequest(
    request: QueuedRequest,
    error: string,
  ): Promise<void> {
    console.error(`Request ${request.id} failed:`, error);
    await this.removeRequest(request.id);
  }

  /**
   * Removes all requests from the queue, effectively clearing it.
   *
   * @returns Promise that resolves when the queue is cleared
   * @throws Error if database is not initialized or clearing fails
   */
  async clearQueue(): Promise<void> {
    if (!this.db) await this.initialize();
    if (!this.db) throw new Error("Failed to initialize IndexedDB");

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Gets the current number of requests in the queue.
   *
   * @returns Promise that resolves to the number of queued requests
   * @throws Error if database is not initialized or count operation fails
   */
  async getQueueSize(): Promise<number> {
    if (!this.db) await this.initialize();
    if (!this.db) throw new Error("Failed to initialize IndexedDB");

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}

/**
 * Singleton instance of the OfflineQueueService.
 * Use this instance throughout your application to maintain a single queue.
 *
 * @example
 * ```typescript
 * import { offlineQueue } from './services/offlineQueue';
 *
 * // Initialize the queue when your app starts
 * await offlineQueue.initialize();
 *
 * // Queue requests when offline
 * await offlineQueue.queueRequest('/api/data', 'POST', {}, { data: 'example' });
 * ```
 */
export const OfflineQueue = new OfflineQueueService();
